import { useId, type KeyboardEvent, type ReactNode } from 'react'

// A screen in parts, one part on the page at a time (10 Oct 2026).
//
// # WHY THIS EXISTS
//
// A screen that stacks a summary, a form and a list is two or three screens
// tall, and the part a reader came for is under the fold: the risk register
// started 700 px down its page, the campaign list under a form nobody was
// filling in. The parts become tabs, so the screen opens on one of them and
// the others are one press away.
//
// # WHAT A TAB MUST CARRY
//
// Stacked, a reader saw a count or a warning by scrolling past it. Behind a
// tab nothing is seen by accident, so a tab shows its count, and a part that
// needs somebody says so on its tab (`alert`). A part with something waiting
// in it must never look the same as an empty one.
//
// # KEYBOARD
//
// One stop in the tab order; the arrow keys, Home and End move between tabs
// and select as they go, which is what a tab list is expected to do.

export type PageTab<K extends string> = {
  key: K
  label: string
  count?: number
  // What in this part needs somebody, in a few words. Read out as part of the
  // tab's name and drawn as a mark on it.
  alert?: string
}

// The row of tabs and the part they open, as two pieces that share an id
// (from useId), for a screen that sets the row beside its title to keep the
// whole of it on one screen. PageTabs below is the two together.
export function PageTabList<K extends string>({
  id,
  label,
  tabs,
  value,
  onChange,
  inline = false,
}: {
  id: string
  label: string
  tabs: PageTab<K>[]
  value: K
  onChange: (next: K) => void
  // In a page's heading row: the tabs alone, without the bar drawn round them.
  inline?: boolean
}) {
  const move = (e: KeyboardEvent<HTMLDivElement>) => {
    // With a modifier the arrows are the browser's (Alt or Cmd with Left is
    // Back), not the tab row's.
    if (e.altKey || e.metaKey || e.ctrlKey) return
    const at = tabs.findIndex((t) => t.key === value)
    const to =
      e.key === 'ArrowRight' ? (at + 1) % tabs.length
      : e.key === 'ArrowLeft' ? (at - 1 + tabs.length) % tabs.length
      : e.key === 'Home' ? 0
      : e.key === 'End' ? tabs.length - 1
      : -1
    if (to < 0 || to === at) return
    e.preventDefault()
    const next = tabs[to]!
    onChange(next.key)
    document.getElementById(id + next.key)?.focus()
  }
  return (
    <div className={inline ? 'kit-tabs kit-tabs-inline' : 'kit-tabs kit-tabs-bar'} role="tablist" aria-label={label} onKeyDown={move}>
      {tabs.map((t) => {
        const on = t.key === value
        return (
          <button
            key={t.key}
            type="button"
            role="tab"
            id={id + t.key}
            data-tab={t.key}
            aria-selected={on}
            aria-controls={id + 'panel'}
            tabIndex={on ? 0 : -1}
            className={'settings-tab' + (on ? ' on' : '')}
            onClick={() => onChange(t.key)}
          >
            {t.label}
            {t.count != null && <span className="kit-tab-count">{t.count}</span>}
            {t.alert && (
              <span className="kit-tab-alert" role="img" aria-label={t.alert}>
                !
              </span>
            )}
          </button>
        )
      })}
    </div>
  )
}

// The part that is open. The caller decides what stays mounted.
export function PageTabPanel({ id, value, children }: { id: string; value: string; children: ReactNode }) {
  return (
    <div role="tabpanel" id={id + 'panel'} aria-labelledby={id + value} data-tab-panel={value}>
      {children}
    </div>
  )
}

export function PageTabs<K extends string>({
  label,
  tabs,
  value,
  onChange,
  children,
}: {
  label: string
  tabs: PageTab<K>[]
  value: K
  onChange: (next: K) => void
  children: ReactNode
}) {
  const id = useId()
  return (
    <>
      <PageTabList id={id} label={label} tabs={tabs} value={value} onChange={onChange} />
      <PageTabPanel id={id} value={value}>
        {children}
      </PageTabPanel>
    </>
  )
}
